import axios from "../utils/axios";

/**
 * List all clients with pagination and advanced filtering
 * @param {Object} params - Query parameters (page, per_page, search, trashed)
 * @returns {Promise} Clients list
 */
export const getClients = async (params) => {
    try {
        const { data } = await axios.get('/clients', { params });
        return data;
    } catch (error) {
        console.error("🚀 ~ getClients ~ error:", error);
        throw error;
    }
};

/**
 * Get a single client by ID
 * @param {number|string} id - Client ID
 * @returns {Promise} Client data
 */
export const getClientById = async (id) => {
    try {
        const { data } = await axios.get(`/clients/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ getClientById ~ error:", error);
        throw error;
    }
};

/**
 * Create a new client
 * @param {Object} clientData - Client data (name_ar, name_en, logo)
 * @returns {Promise} Created client
 */
export const createClient = async (clientData) => {
    try {
        const { data } = await axios.post('/clients', clientData);
        return data;
    } catch (error) {
        console.error("🚀 ~ createClient ~ error:", error);
        throw error;
    }
};

/**
 * Update an existing client
 * @param {number|string} id - Client ID
 * @param {Object} clientData - Updated client data
 * @returns {Promise} Updated client
 */
export const updateClient = async (id, clientData) => {
    try {
        const { data } = await axios.put(`/clients/${id}`, clientData);
        return data;
    } catch (error) {
        console.error("🚀 ~ updateClient ~ error:", error);
        throw error;
    }
};

/**
 * Soft delete a client
 * @param {number|string} id - Client ID
 * @returns {Promise} Result of deletion
 */
export const deleteClient = async (id) => {
    try {
        const { data } = await axios.delete(`/clients/${id}`);
        return data;
    } catch (error) {
        console.error("🚀 ~ deleteClient ~ error:", error);
        throw error;
    }
};

/**
 * Force delete a client (permanent)
 * @param {number|string} id - Client ID
 * @returns {Promise} Result of deletion
 */
export const forceDeleteClient = async (id) => {
    try {
        const { data } = await axios.delete(`/clients/${id}/force-delete`);
        return data;
    } catch (error) {
        console.error("🚀 ~ forceDeleteClient ~ error:", error);
        throw error;
    }
};

/**
 * Restore a soft-deleted client
 * @param {number|string} id - Client ID
 * @returns {Promise} Restored client
 */
export const restoreClient = async (id) => {
    try {
        const { data } = await axios.post(`/clients/${id}/restore`);
        return data;
    } catch (error) {
        console.error("🚀 ~ restoreClient ~ error:", error);
        throw error;
    }
};

/**
 * Bulk soft delete clients
 * @param {Array} items - Array of client IDs
 * @returns {Promise} Result
 */
export const massDestroyClients = async (data) => {
    try {
        const response = await axios.delete('/clients/mass-destroy', { data });
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massDestroyClients ~ error:", error);
        throw error;
    }
};

/**
 * Bulk force delete clients
 * @param {Array} items - Array of client IDs
 * @returns {Promise} Result
 */
export const massForceDeleteClients = async (data) => {
    try {
        const response = await axios.delete('/clients/mass-force-delete', { data });
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massForceDeleteClients ~ error:", error);
        throw error;
    }
};

/**
 * Bulk restore clients
 * @param {Array} items - Array of client IDs
 * @returns {Promise} Result
 */
export const massRestoreClients = async (data) => {
    try {
        const response = await axios.post('/clients/mass-restore', data);
        return response.data;
    } catch (error) {
        console.error("🚀 ~ massRestoreClients ~ error:", error);
        throw error;
    }
};

/**
 * Get clients for selection (simplified list)
 * Usually used for Client dropdown in projects
 * @param {Object} params - optional filters
 * @returns {Promise} List for dropdown
 */
export const getClientsForSelect = async (params) => {
    try {
        const { data } = await axios.get('/clients', {
            params: { ...params, per_page: 100 }
        });
        return data;
    } catch (error) {
        console.error("🚀 ~ getClientsForSelect ~ error:", error);
        throw error;
    }
};